import { Component, Input, ChangeDetectorRef, ElementRef, EventEmitter, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as Delta from 'quill-delta';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { webVideoHelper } from '../../../../Common/Misc/helpers/webVideo/webVideo.helper';

const iframeEmbed = `
<iframe
    width="560"
    height="315"
    src="%SRCURL%"
    frameborder="0"
    allow="accelerometer; encrypted-media; gyroscope"
    allowfullscreen
></iframe>
`;
/* eslint-disable @typescript-eslint/naming-convention */
const EditableValueProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VideoInputComponent),
    multi: true
};

@Component({
    selector: 'agpr-video-input',
    templateUrl: './video-input.component.html',
    styleUrls: ['./video-input.component.scss'],
    providers: [EditableValueProvider],
    standalone: false
})
export class VideoInputComponent implements ControlValueAccessor {
    @Input() placeholder: string;
    @Input() placeholderIcon: string;
    @Input() invalidFormatMessage: string;
    @Input() showDelete: boolean = false;
    @Input() showMoveButtons: boolean = false;
    @Input() moveUpDisabled: boolean = false;
    @Input() moveDownDisabled: boolean = false;
    @Input() isDisabled: boolean = false;
    @Input() maxLength: number = 0;
    @Output() deleteClick: EventEmitter<any> = new EventEmitter();
    @Output() moveUp: EventEmitter<any> = new EventEmitter();
    @Output() moveDown: EventEmitter<any> = new EventEmitter();

    public editable: boolean = false;
    public content: string = '';
    public safeEmbedCode: SafeHtml;
    constructor(
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        private sanitizer: DomSanitizer
    ) {}

    registerOnTouched(fn: () => void): void { }
    propagateChange: any = () => { };

    writeValue(value: any): void {
        this.content = value;
        if (this.isValidURL()) {
            this.content = this.getEmbedURL();
            this.safeEmbedCode = this.getSafeEmbedCode();
        }
    }

    registerOnChange(fn): void {
        this.propagateChange = fn;
    }

    public onInit(event): void {
        const limit: number = +this.maxLength;
        event.editor.on('text-change', function (delta, old, source) {
            if (event.editor.getLength() > limit) {
                event.editor.deleteText(limit, event.editor.getLength());
            }
        });
    }

    public onInputBlurred(event: Event): void {
        this.editable = false;
        if (this.isValidURL()) {
            this.safeEmbedCode = this.getSafeEmbedCode();
        } else {
            this.safeEmbedCode = null;
        }
        this.changeDetector.detectChanges();
    }

    public onValueChanged(event): void {
        if (this.isValidURL()) {
            this.propagateChange(this.getEmbedCode());
        } else {
            this.propagateChange(this.content);
        }
    }

    public onEdit($event): void {
        if (this.isDisabled) {
            return;
        }
        if (this.content === undefined ||
            this.content === null ||
            this.content === ''
        ) {
            this.content = '';
        }
        this.editable = true;
        this.changeDetector.detectChanges();
    }

    public onDelete($event): void {
        if (this.isDisabled) {
            return;
        }
        this.content = '';
        this.editable = false;
        this.changeDetector.detectChanges();
        this.deleteClick.emit($event);
    }

    public onMoveUp(): void {
        this.moveUp.emit();
    }
    public onMoveDown(): void {
        this.moveDown.emit();
    }

    public isValidURL(): boolean {
        const content = this.content;
        if (!content) {
            return false;
        }
        if (webVideoHelper.isYoutubeURL(content)) {
            return true;
        }
        return false;
    }
    public getEmbedURL(): string {
        return webVideoHelper.getYoutubeEmbedURL(this.content);
    }
    public getEmbedCode(): string {
        const url = this.getEmbedURL();
        if (!url) {
            return '';
        }
        const html = iframeEmbed.replace('%SRCURL%', url);
        return html;
    }
    public getSafeEmbedCode(): SafeHtml {
        const html = this.getEmbedCode();
        const sanitized = this.sanitizer.bypassSecurityTrustHtml(html);
        return sanitized;
    }
}
